import {useState, useMemo, useEffect, useCallback, useRef} from 'react';
import {useMembersTranslation} from '@/i18n/hooks';
import {useAuth} from '@/context/use-auth';
import {Input, InputGroup} from '@/components/tailwind/input';
import {Select} from '@/components/tailwind/select';
import {Field, Label} from '@/components/tailwind/fieldset';
import {Button} from '@/components/tailwind/button';
import {Link} from '@/components/tailwind/link';
import {MagnifyingGlassIcon} from '@heroicons/react/16/solid';
import {ArrowUpDown, ArrowDownAZ, ArrowUpAZ} from 'lucide-react';
import {SpecialAssignmentService, SpecialAssignmentMemberItem, CreateSpecialAssignmentBookingRequest, getMembersForSpecialAssignment} from '@/services/special-assignment-services';
import log from '@/lib/logger'
import SpecialAssignmentBookingDialog from '@/components/special-assignment-booking-dialog';
import {DataTable} from '@/components/data-table';
import {ColumnDef, Column} from '@tanstack/react-table';

interface SelectAllCheckboxProps {
    filteredMembers: SpecialAssignmentMemberItem[];
    selectedMemberIds: Set<string>;
    onSelectAll: () => void;
    onDeselectAll: () => void;
}

function SelectAllCheckbox({filteredMembers, selectedMemberIds, onSelectAll, onDeselectAll}: SelectAllCheckboxProps) {
    const ref = useRef<HTMLInputElement>(null);
    const allSelected = filteredMembers.length > 0 && filteredMembers.every(m => selectedMemberIds.has(m.id));
    const someSelected = !allSelected && filteredMembers.some(m => selectedMemberIds.has(m.id));

    useEffect(() => {
        if (ref.current) {
            ref.current.indeterminate = someSelected;
        }
    }, [someSelected]);

    return (
        <input
            ref={ref}
            type="checkbox"
            checked={allSelected}
            onChange={() => allSelected ? onDeselectAll() : onSelectAll()}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 dark:border-gray-600"
            aria-label="Alle auswählen"
            data-testid="select-all-checkbox"
        />
    );
}

function sortableHeader<TData, TValue>(column: Column<TData, TValue>, label: string) {
    return (
        <Link
            href="#"
            role="button"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
            {label}
            {column.getIsSorted() === false && <ArrowUpDown size={12} className="inline ml-1"/>}
            {column.getIsSorted() === 'asc' && <ArrowDownAZ size={12} className="inline ml-1"/>}
            {column.getIsSorted() === 'desc' && <ArrowUpAZ size={12} className="inline ml-1"/>}
        </Link>
    );
}

interface SpecialAssignmentBookingTabProps {
    services: SpecialAssignmentService[];
    onBookingCreated: () => void;
    onConfirm: (request: CreateSpecialAssignmentBookingRequest) => Promise<void>;
}

export default function SpecialAssignmentBookingTab({
    services,
    onBookingCreated,
    onConfirm,
}: SpecialAssignmentBookingTabProps) {
    const {t} = useMembersTranslation();
    const {token} = useAuth();

    const [selectedServiceId, setSelectedServiceId] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [members, setMembers] = useState<SpecialAssignmentMemberItem[]>([]);

    useEffect(() => {
        if (!token) return;
        const controller = new AbortController();
        getMembersForSpecialAssignment(token, controller.signal)
            .then(setMembers)
            .catch((err: unknown) => {
                if (err instanceof Error && err.name !== 'AbortError') {
                    log.warn('Failed to load members for special assignments', err);
                }
            });
        return () => controller.abort();
    }, [token]);

    const filteredMembers = useMemo(() => {
        let result = members;
        if (selectedServiceId) {
            result = result.filter(m => (m.serviceIds ?? []).includes(selectedServiceId));
        }
        if (searchTerm.trim()) {
            const lower = searchTerm.toLowerCase();
            result = result.filter(m =>
                m.firstName.toLowerCase().includes(lower) ||
                m.lastName.toLowerCase().includes(lower) ||
                String(m.memberNo).includes(lower)
            );
        }
        return result;
    }, [members, searchTerm, selectedServiceId]);

    function handleServiceChange(serviceId: string) {
        setSelectedServiceId(serviceId);
        setSelectedMemberIds(new Set());
    }

    const handleSelectAll = useCallback(() => {
        setSelectedMemberIds(new Set(filteredMembers.map(m => m.id)));
    }, [filteredMembers]);

    const handleDeselectAll = useCallback(() => {
        setSelectedMemberIds(new Set());
    }, []);

    const handleToggleMember = useCallback((memberId: string) => {
        setSelectedMemberIds(prev => {
            const next = new Set(prev);
            if (next.has(memberId)) {
                next.delete(memberId);
            } else {
                next.add(memberId);
            }
            return next;
        });
    }, []);

    async function handleConfirm(request: CreateSpecialAssignmentBookingRequest) {
        await onConfirm(request);
        setSelectedMemberIds(new Set());
        onBookingCreated();
    }

    const columns = useMemo<ColumnDef<SpecialAssignmentMemberItem>[]>(() => [
        {
            id: 'select',
            enableSorting: false,
            header: () => (
                <SelectAllCheckbox
                    filteredMembers={filteredMembers}
                    selectedMemberIds={selectedMemberIds}
                    onSelectAll={handleSelectAll}
                    onDeselectAll={handleDeselectAll}
                />
            ),
            cell: ({row}) => (
                <input
                    type="checkbox"
                    checked={selectedMemberIds.has(row.original.id)}
                    onChange={() => handleToggleMember(row.original.id)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 dark:border-gray-600"
                    aria-label={`${row.original.firstName} ${row.original.lastName} auswählen`}
                    data-testid={`member-checkbox-${row.original.id}`}
                />
            ),
        },
        {
            accessorKey: 'memberNo',
            header: ({column}) => sortableHeader(column, t('specialAssignments.memberTable.headers.memberNo')),
        },
        {
            accessorKey: 'firstName',
            header: ({column}) => sortableHeader(column, t('specialAssignments.memberTable.headers.firstName')),
        },
        {
            accessorKey: 'lastName',
            header: ({column}) => sortableHeader(column, t('specialAssignments.memberTable.headers.lastName')),
        },
    ], [selectedMemberIds, filteredMembers, handleToggleMember, handleSelectAll, handleDeselectAll, t]);

    const selectedCount = selectedMemberIds.size;

    return (
        <div className="relative pb-20">
            <div className="space-y-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                    <Field className="sm:w-64">
                        <Label>{t('specialAssignments.filter.serviceFilter')}</Label>
                        <Select
                            value={selectedServiceId}
                            onChange={(e) => handleServiceChange(e.target.value)}
                            data-testid="service-filter-select"
                        >
                            <option value="">{t('specialAssignments.filter.allServices')}</option>
                            {services.map(service => (
                                <option key={service.id} value={service.id}>
                                    {service.name}
                                </option>
                            ))}
                        </Select>
                    </Field>

                    <div className="flex-1">
                        <InputGroup>
                            <MagnifyingGlassIcon/>
                            <Input
                                type="text"
                                placeholder={t('specialAssignments.memberList.search')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                data-testid="member-search-input"
                            />
                        </InputGroup>
                    </div>
                </div>

                <DataTable
                    columns={columns}
                    data={filteredMembers}
                    resetPageKey={`${selectedServiceId}|${searchTerm}`}
                    data-testid="member-list"
                />
            </div>

            {selectedCount > 0 && (
                <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-gray-200 bg-white px-4 py-3 shadow-lg dark:border-gray-700 dark:bg-gray-900 sm:px-6">
                    <div className="mx-auto flex max-w-7xl items-center justify-between">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t('specialAssignments.bottomBar.selected', {count: selectedCount})}
                        </span>
                        <Button
                            onClick={() => setIsDialogOpen(true)}
                            data-testid="book-points-button"
                        >
                            {t('specialAssignments.bottomBar.bookButton')}
                        </Button>
                    </div>
                </div>
            )}

            <SpecialAssignmentBookingDialog
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onConfirm={handleConfirm}
                selectedMemberIds={Array.from(selectedMemberIds)}
                availableServices={services}
                preSelectedServiceId={selectedServiceId || undefined}
            />
        </div>
    );
}
